import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
}                                                     from '@mui/material'
import {Check, DarkMode, LightMode, SettingsBrightness} from '@mui/icons-material'
import {useUserStore, useThemeStore}                  from '../../state'
import {doPost}                                       from '../../util/do-fetch'
import {fetchUserProfile}                             from '../../util/mini'
import {currentTutorStyle, fetchTutorStyles, TUTOR_STYLE_LABEL} from '../../util/tutor-style'
import {THEME_AUTO, THEME_DARK, THEME_LIGHT}          from '../../util/theme-preference'

/*
 * First-run setup, as one modal (196).
 *
 * Shown when a student opens a PrepKit without a complete profile -- not welcomed, or missing a
 * name, or with no tutor style chosen. Mounted once in PrepKit.js, which wraps the dashboard AND
 * every study screen through its <Outlet/>, so landing straight on a question screen from a shared
 * link is covered too. The dashboard's inline Welcome only ever covered the dashboard.
 *
 * SELF-SUFFICIENT: it reads the user from the store and fetches the profile itself, the same pattern
 * TutorStylePicker uses when it has no parent to hand it one. That keeps PrepKit.js to a one-line
 * change rather than making it hold profile state it does not otherwise want.
 *
 * NOT DISMISSABLE. No backdrop close, no Escape, no X. The whole point is that these fields get
 * filled; an onboarding step that can be waved away is one that does not happen.
 */

// what "set up" means -- all four, not just welcomed_at
const isProfileComplete = (profile) => Boolean(
	profile
	&& profile.welcomed_at
	&& (profile.first_name || '').trim()
	&& (profile.last_name || '').trim()
	&& profile.ai_personality_id
)

const THEME_CHOICES = [
	{value: THEME_LIGHT, label: 'Light', Icon: LightMode},
	{value: THEME_DARK,  label: 'Dark',  Icon: DarkMode},
	{value: THEME_AUTO,  label: 'Auto',  Icon: SettingsBrightness},
]

/*
 * One tutor style as a selectable card. The description is the personality's own copy from the
 * ai_personality table, so a student reads what an admin wrote on that screen.
 */
const StyleCard = ({style, custom, selected, onSelect}) => (
	<Box
		onClick={() => onSelect(style.id)}
		sx={{
			cursor: 'pointer',
			height: '100%',
			p: 1.75,
			borderRadius: `${custom.radius.md}px`,
			border: `1px solid ${selected ? custom.indigo.border : custom.border.color}`,
			background: selected ? custom.bg.raised : custom.bg.cardInner,
			transition: 'border-color .15s, background .15s',
			'&:hover': {borderColor: custom.indigo.border},
		}}
	>
		<Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5}}>
			<Typography sx={{
				flex: 1,
				fontSize: custom.fontSize.base,
				fontWeight: custom.fontWeight.semibold,
				color: custom.text.main,
			}}>
				{style.name}
			</Typography>
			{selected && <Check sx={{fontSize: 18, color: custom.indigo.primary}}/>}
		</Box>
		<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, lineHeight: 1.45}}>
			{style.description}
		</Typography>
	</Box>
)

const ProfileSetupDialog = () => {
	const theme = useTheme()
	const custom = theme.custom
	const {user, authLoaded} = useUserStore()
	const {preference, setPreference} = useThemeStore()

	const [profile, setProfile] = useState(null)
	const [styles, setStyles] = useState([])
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [styleId, setStyleId] = useState(null)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [done, setDone] = useState(false)

	// one load per signed-in session, and a guard against StrictMode double-invocation
	const loadedFor = useRef(null)

	useEffect(() => {
		if (!authLoaded || !user) return
		if (loadedFor.current === user.id) return
		loadedFor.current = user.id

		let active = true

		const load = async () => {
			try {
				const loaded = await fetchUserProfile(user.id)
				if (!active) return

				setProfile(loaded)
				setFirstName((loaded && loaded.first_name) || '')
				setLastName((loaded && loaded.last_name) || '')

				// only the incomplete case needs the styles, so they are fetched after the check
				if (isProfileComplete(loaded)) return

				const list = await fetchTutorStyles()
				if (!active) return

				setStyles(list || [])
				/*
				 * Pre-selected to the style they are ALREADY being taught in. currentTutorStyle falls
				 * back to the first entry, which is the same rule the server uses when a profile has
				 * no id set -- so this cannot misreport what the AI is actually doing.
				 */
				const current = currentTutorStyle(list || [], loaded && loaded.ai_personality_id)
				setStyleId(current && current.id)
			}
			catch (err) {
				// never block the app on this: a student with a complete profile is unaffected, and one
				// without will be asked again on the next visit
				console.error('Could not load profile setup', err)
			}
		}

		load()
		return () => { active = false }
	}, [authLoaded, user])

	const save = useCallback(async () => {
		setSaving(true)
		setError(null)
		try {
			const response = await doPost('/mini/user/profile', {
				body: JSON.stringify({
					first_name: firstName.trim(),
					last_name: lastName.trim(),
					ai_personality_id: styleId,
					// stamps welcomed_at server-side
					welcomed: true,
				}),
			})
			if (!response.ok) throw new Error(await response.text())

			const result = await response.json()
			if (result && result.success === false) throw new Error('Could not save your details.')

			setDone(true)
		}
		catch (err) {
			console.error('Could not save profile setup', err)
			setError('Could not save your details. Please try again.')
		}
		finally {
			setSaving(false)
		}
	}, [firstName, lastName, styleId])

	const open = useMemo(
		() => Boolean(profile) && !isProfileComplete(profile) && !done,
		[profile, done]
	)

	const canSave = Boolean(firstName.trim() && lastName.trim() && styleId) && !saving

	if (!open) return null

	return (
		<Dialog
			open
			maxWidth="sm"
			fullWidth
			// no onClose: this cannot be dismissed without answering -- see the note above
			PaperProps={{sx: {borderRadius: `${custom.radius.lg}px`, background: custom.bg.surface}}}
		>
			<DialogContent sx={{p: {xs: 2.5, sm: 4}}}>

				<Typography sx={{
					fontSize: custom.fontSize.display,
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
				}}>
					Welcome to Birdsy
				</Typography>
				<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.muted, mt: 0.5, mb: 3}}>
					Just a few things before you start studying.
				</Typography>

				{error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>{error}</Alert>}

				{/* name */}
				<Typography sx={{fontSize: custom.fontSize.lg, fontWeight: custom.fontWeight.semibold, mb: 1.5}}>
					What should we call you?
				</Typography>
				<Grid container spacing={2} sx={{mb: 3.5}}>
					<Grid item xs={12} sm={6}>
						<TextField
							label="First name"
							fullWidth
							size="small"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							autoFocus
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label="Last name"
							fullWidth
							size="small"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
					</Grid>
				</Grid>

				{/* tutor style */}
				<Typography sx={{fontSize: custom.fontSize.lg, fontWeight: custom.fontWeight.semibold}}>
					Pick your {TUTOR_STYLE_LABEL.toLowerCase()}
				</Typography>
				<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, mb: 1.5}}>
					How Birdsy talks to you while you study. You can change it any time.
				</Typography>

				{styles.length === 0
					? <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}><CircularProgress size={22}/></Box>
					: (
						<Grid container spacing={1.5} sx={{mb: 3.5}}>
							{styles.map(style => (
								<Grid item xs={12} sm={6} key={style.id}>
									<StyleCard
										style={style}
										custom={custom}
										selected={styleId === style.id}
										onSelect={setStyleId}
									/>
								</Grid>
							))}
						</Grid>
					)}

				{/* theme */}
				<Typography sx={{fontSize: custom.fontSize.lg, fontWeight: custom.fontWeight.semibold}}>
					Light or dark?
				</Typography>
				<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, mb: 1.5}}>
					Auto follows your device setting.
				</Typography>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={preference}
					// applies immediately, so the choice is seen rather than described
					onChange={(e, value) => value && setPreference(value)}
					sx={{mb: 3.5}}
				>
					{THEME_CHOICES.map(({value, label, Icon}) => (
						<ToggleButton key={value} value={value} sx={{textTransform: 'none', px: 2}}>
							<Icon sx={{fontSize: 18, mr: 0.75}}/>
							{label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>

				<Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
					<Button variant="contained" disableElevation onClick={save} disabled={!canSave}>
						{saving ? 'Saving…' : 'Start studying'}
						{saving && <CircularProgress size={14} sx={{ml: 1}}/>}
					</Button>
				</Box>

			</DialogContent>
		</Dialog>
	)
}

export default ProfileSetupDialog
export {isProfileComplete}
